import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';
import defaultSpeakersData from '../data/speakersData.json';

const Speakers = () => {
    const [speakers, setSpeakers] = useState(defaultSpeakersData);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    const { config } = useConfig();

    useEffect(() => {
        const fetchSpeakers = async () => {
            try {
                const response = await api.get('/speakers?status=active');
                if (response.data && response.data.length > 0) {
                    // Si el backend tiene conferencistas, los usamos, sino usamos los 20 oficiales del PDF
                    setSpeakers(response.data);
                }
            } catch (error) {
                // Silently fallback to official static speakers
                console.log("Using official PDF speakers data");
            }
        };
        fetchSpeakers();
    }, []);

    // Función auxiliar para renderizar el nombre con saltos estilizados (Dr./Dra. arriba o nombres)
    const formatSpeakerName = (fullName) => {
        // Separa sufijos de honores como ', PhD, FAHA...' para mantener el nombre limpio
        const parts = fullName.split(',');
        const mainName = parts[0].trim();
        const suffix = parts.slice(1).join(', ').trim();

        return (
            <div>
                <span>{mainName}</span>
                {suffix && <span className="block text-xs font-semibold text-white/80 mt-0.5">{suffix}</span>}
            </div>
        );
    };

    return (
        <div className="animate-fadeIn pb-24 font-sans max-w-4xl mx-auto px-4">
            {/* Título de Sección: PONENTES (Página 5 PDF) */}
            <header className="mb-10 text-center">
                <h1 className="text-3xl sm:text-4xl font-black text-[#ce0f46] tracking-wider uppercase">
                    PONENTES
                </h1>
            </header>

            {/* Grilla de 2 columnas de conferencistas (Página 5 PDF) */}
            <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-10 sm:gap-y-14">
                {speakers.map((s) => (
                    <div
                        key={s.id || s.name}
                        className="flex flex-col items-center text-center group cursor-pointer"
                        onClick={() => setSelectedSpeaker(s)}
                    >
                        {/* Contenedor de la foto del ponente con halo de luz circular ya integrado en la imagen */}
                        <div className="relative mb-3 w-32 h-32 sm:w-44 sm:h-44 transition-transform duration-300 group-hover:scale-105">
                            <img
                                src={s.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(s.name)}&background=random`}
                                alt={s.name}
                                className="w-full h-full object-contain drop-shadow-xl"
                            />
                        </div>

                        {/* Nombre del ponente */}
                        <h3 className="text-[14px] sm:text-[18px] font-black text-white leading-snug tracking-tight px-1 transition-colors group-hover:text-red-300">
                            {formatSpeakerName(s.name)}
                        </h3>
                    </div>
                ))}
            </div>

            {/* Modal de Detalle del Ponente (Página 6 PDF) */}
            {selectedSpeaker && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-fadeIn">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/85 backdrop-blur-md"
                        onClick={() => setSelectedSpeaker(null)}
                    ></div>

                    {/* Modal Content: Tarjeta redondeada con borde fucsia #c20042 */}
                    <div
                        style={{
                            borderColor: '#c20042',
                            backgroundColor: '#550043',
                        }}
                        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-[3rem] border-2 shadow-[0_25px_60px_rgba(0,0,0,0.8)] animate-scaleUp p-6 sm:p-10 custom-scrollbar text-white"
                    >
                        {/* Botón de cierre en rosa #f61170 */}
                        <button
                            className="absolute top-6 right-8 text-[#f61170] hover:text-white text-3xl font-black transition-colors z-10"
                            onClick={() => setSelectedSpeaker(null)}
                        >
                            ✕
                        </button>

                        <div className="flex flex-col items-center text-center">
                            {/* Imagen del ponente con halo */}
                            <div className="w-36 h-36 sm:w-48 sm:h-48 mb-4">
                                <img
                                    src={selectedSpeaker.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedSpeaker.name)}&background=random`}
                                    alt={selectedSpeaker.name}
                                    className="w-full h-full object-contain drop-shadow-2xl"
                                />
                            </div>

                            {/* Nombre del ponente en #ce0f46 */}
                            <h2 className="text-2xl sm:text-3xl font-black text-[#ce0f46] mb-6 leading-tight max-w-md">
                                {selectedSpeaker.name}
                            </h2>

                            {/* Biografía / Viñetas */}
                            <div className="text-white/90 leading-relaxed text-sm sm:text-base text-left w-full space-y-4">
                                {selectedSpeaker.bio_points && selectedSpeaker.bio_points.length > 0 ? (
                                    selectedSpeaker.bio_points.map((pt, i) => (
                                        <div key={i} className="flex items-start gap-2.5">
                                            <span className="text-[#ce0f46] text-lg leading-none mt-0.5">•</span>
                                            <span className="flex-1 font-medium">{pt}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="whitespace-pre-wrap font-medium">
                                        {selectedSpeaker.profile || selectedSpeaker.profession || "Información no disponible."}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Speakers;
